import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";

interface AnimatedStreamingTextProps {
  text: string;
  isStreaming?: boolean;
  minHeight?: number;
  maxHeight?: number;
  fontSize?: string | number;
  fontFamily?: string;
  lineHeight?: number | string;
  color?: string;
  px?: number;
  py?: number;
}

const AnimatedStreamingText: React.FC<AnimatedStreamingTextProps> = ({
  text,
  isStreaming = false,
  minHeight,
  maxHeight,
  fontSize = "0.96rem",
  fontFamily,
  lineHeight = 1.75,
  color = "rgba(226, 232, 240, 0.92)",
  px = 0,
  py = 0,
}) => {
  const [visibleLength, setVisibleLength] = useState(text.length);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);

  const isNearBottom = useCallback((element: HTMLDivElement) => {
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= 28;
  }, []);

  useEffect(() => {
    if (text.length < visibleLength) {
      setVisibleLength(text.length);
      return;
    }

    if (!isStreaming) {
      setVisibleLength(text.length);
      return;
    }

    if (visibleLength >= text.length) {
      return;
    }

    let timeoutId: number | undefined;

    const tick = () => {
      setVisibleLength((current) => {
        if (current >= text.length) {
          return current;
        }

        const remaining = text.length - current;
        const step = Math.min(Math.max(1, Math.ceil(remaining / 20)), 5);
        return Math.min(text.length, current + step);
      });

      timeoutId = window.setTimeout(tick, 35);
    };

    timeoutId = window.setTimeout(tick, 35);

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [text, isStreaming, visibleLength]);

  const visibleText = useMemo(() => {
    if (!isStreaming) {
      return text;
    }

    return text.slice(0, visibleLength);
  }, [isStreaming, text, visibleLength]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    if (shouldStickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [visibleText]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    shouldStickToBottomRef.current = isNearBottom(event.currentTarget);
  }, [isNearBottom]);

  return (
    <Box
      ref={scrollContainerRef}
      onScroll={handleScroll}
      sx={{
        position: "relative",
        px,
        py,
        minHeight,
        maxHeight,
        overflowY: "auto",
        color,
        whiteSpace: "pre-wrap",
        lineHeight,
        fontSize,
        fontFamily,
        "@keyframes streamingCaretBlink": {
          "0%, 45%": { opacity: 1 },
          "55%, 100%": { opacity: 0.15 },
        },
      }}
    >
      {visibleText}
      {isStreaming && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "inline-block",
            width: "0.7ch",
            height: "1.15em",
            ml: "1px",
            verticalAlign: "text-bottom",
            borderRadius: "2px",
            background: "linear-gradient(180deg, rgba(44,192,183,0.95), rgba(103,232,249,0.85))",
            boxShadow: "0 0 12px rgba(44,192,183,0.25)",
            animation: "streamingCaretBlink 0.95s ease-in-out infinite",
          }}
        />
      )}
    </Box>
  );
};

export default AnimatedStreamingText;
